import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  DocumentTextIcon, TrashIcon, ArrowDownTrayIcon,
  PlusIcon, MagnifyingGlassIcon, EyeIcon, ArrowPathIcon
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { ButtonGroup } from '../components/ui/button-group';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { confirmDialog } from '../components/ConfirmDialog';
import { Tip } from '../components/ui/tip';

interface Manual {
  id: string;
  code?: string;
  tipo?: string;
  area?: string;
  correlativo?: number;
  revision?: number;
  status?: string;          // vigente | obsoleto
  classification?: string;  // publico | uso_interno | confidencial
  title: string;
  description?: string;
  category: string;
  fileName: string;
  filePath: string;
  fileSize: number;
  mimeType?: string;
  visibleToRoles?: string[];
  createdAt: string;
}

// Norma MA-CAL-01: TIPO-ÁREA-Nº-REV
const TIPOS: { value: string; label: string }[] = [
  { value: 'MA', label: 'MA · Manual' },
  { value: 'PR', label: 'PR · Procedimiento' },
  { value: 'IT', label: 'IT · Instructivo de Trabajo' },
  { value: 'IM', label: 'IM · Instructivo de Mantenimiento' },
  { value: 'ES', label: 'ES · Especificación' },
  { value: 'PL', label: 'PL · Plan' },
  { value: 'FR', label: 'FR · Formulario / Registro' },
  { value: 'PO', label: 'PO · Política' },
  { value: 'EQ', label: 'EQ · Ficha de equipo' },
];
const AREAS: { value: string; label: string }[] = [
  { value: 'ADM', label: 'ADM · Administración' },
  { value: 'CAL', label: 'CAL · Calidad' },
  { value: 'COM', label: 'COM · Comercial' },
  { value: 'DET', label: 'DET · Detailing' },
  { value: 'EQ', label: 'EQ · Equipos' },
  { value: 'GOM', label: 'GOM · Gomería' },
  { value: 'LAV', label: 'LAV · Lavadero' },
  { value: 'LUB', label: 'LUB · Lubricentro' },
  { value: 'MEC', label: 'MEC · Mecánica' },
  { value: 'RH', label: 'RH · Recursos Humanos' },
];
const AREA_LABEL: Record<string, string> = Object.fromEntries(
  AREAS.map(a => [a.value, a.label.split(' · ')[1] || a.value])
);
const CLASSIFICATIONS: { value: string; label: string }[] = [
  { value: 'publico', label: 'Público' },
  { value: 'uso_interno', label: 'Uso interno' },
  { value: 'confidencial', label: 'Confidencial' },
];
const CLASS_LABEL: Record<string, string> = Object.fromEntries(CLASSIFICATIONS.map(c => [c.value, c.label]));
const CLASS_BADGE: Record<string, string> = {
  publico: 'bg-sky-500/15 text-sky-600 dark:text-sky-400',
  uso_interno: 'bg-slate-500/15 text-slate-600 dark:text-slate-300',
  confidencial: 'bg-red-500/15 text-red-600 dark:text-red-400',
};

const pad2 = (n: number) => String(n).padStart(2, '0');
const fullCodeOf = (m: Manual): string => {
  if (m.tipo && m.area && m.correlativo != null) {
    const base = `${m.tipo}-${m.area}-${pad2(m.correlativo)}`;
    // La emisión inicial (revision 0) no lleva sufijo; la 1ª revisión es R01.
    return (m.revision && m.revision >= 1) ? `${base}-R${pad2(m.revision)}` : base;
  }
  return m.code || '—';
};

const fmtSize = (b: number) => b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`;

const ManualsPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('manage_manuals') || hasPermission('manage_settings');

  const [manuals, setManuals] = useState<Manual[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterArea, setFilterArea] = useState('all');
  const [filterStatus, setFilterStatus] = useState('vigente');
  const [search, setSearch] = useState('');

  const [availableRoles, setAvailableRoles] = useState<{ id: string; name: string }[]>([]);

  // Alta de documento nuevo
  const [showNew, setShowNew] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ tipo: 'IT', area: 'DET', correlativo: '', revision: '0', title: '', description: '', classification: 'uso_interno' });
  const [newFile, setNewFile] = useState<File | null>(null);
  const [newRoles, setNewRoles] = useState<string[]>([]);
  const newFileRef = useRef<HTMLInputElement>(null);

  // Nueva revisión
  const [revTarget, setRevTarget] = useState<Manual | null>(null);
  const [revForm, setRevForm] = useState({ title: '', description: '', classification: 'uso_interno' });
  const [revFile, setRevFile] = useState<File | null>(null);
  const [revUploading, setRevUploading] = useState(false);
  const revFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    axios.get('/api/roles').then(res => {
      setAvailableRoles((res.data.roles || res.data || []).filter((r: any) => r.name !== 'admin'));
    }).catch(() => {});
  }, []);

  const fetchManuals = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterArea !== 'all') params.set('area', filterArea);
      if (canManage && filterStatus) params.set('status', filterStatus);
      const res = await axios.get(`/api/manuals?${params.toString()}`);
      setManuals(res.data.manuals || []);
    } catch {
      toast.error('Error al cargar documentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchManuals(); /* eslint-disable-next-line */ }, [filterArea, filterStatus]);

  const filtered = manuals.filter(m =>
    !search.trim() ||
    m.title.toLowerCase().includes(search.toLowerCase()) ||
    fullCodeOf(m).toLowerCase().includes(search.toLowerCase()) ||
    (m.description || '').toLowerCase().includes(search.toLowerCase())
  );

  // Sugerir próximo correlativo al cambiar tipo/área en el alta
  useEffect(() => {
    if (!showNew || !form.tipo || !form.area) return;
    let cancel = false;
    axios.get(`/api/manuals/next-correlativo?tipo=${form.tipo}&area=${form.area}`)
      .then(res => { if (!cancel) setForm(f => ({ ...f, correlativo: String(res.data.correlativo) })); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [showNew, form.tipo, form.area]);

  const openNew = () => {
    setForm({ tipo: 'IT', area: 'DET', correlativo: '', revision: '0', title: '', description: '', classification: 'uso_interno' });
    setNewFile(null);
    setNewRoles([]);
    setShowNew(true);
  };

  const handleUpload = async () => {
    if (!form.title.trim()) { toast.error('El título es obligatorio'); return; }
    if (!newFile) { toast.error('Seleccioná un archivo'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', newFile);
      fd.append('tipo', form.tipo);
      fd.append('area', form.area);
      if (form.correlativo.trim()) fd.append('correlativo', form.correlativo.trim());
      fd.append('revision', form.revision || '0');
      fd.append('title', form.title.trim());
      fd.append('description', form.description.trim());
      fd.append('classification', form.classification);
      fd.append('visibleToRoles', JSON.stringify(newRoles));
      await axios.post('/api/manuals', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Documento creado');
      setShowNew(false);
      fetchManuals();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al subir');
    } finally {
      setUploading(false);
    }
  };

  const openRevision = (m: Manual) => {
    setRevTarget(m);
    setRevForm({ title: m.title, description: m.description || '', classification: m.classification || 'uso_interno' });
    setRevFile(null);
  };

  const handleRevision = async () => {
    if (!revTarget) return;
    if (!revFile) { toast.error('Seleccioná el archivo de la nueva revisión'); return; }
    setRevUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', revFile);
      fd.append('title', revForm.title.trim());
      fd.append('description', revForm.description.trim());
      fd.append('classification', revForm.classification);
      await axios.post(`/api/manuals/${revTarget.id}/revision`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Nueva revisión publicada · la anterior quedó obsoleta');
      setRevTarget(null);
      fetchManuals();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al crear la revisión');
    } finally {
      setRevUploading(false);
    }
  };

  const handleDelete = async (m: Manual) => {
    if (!(await confirmDialog({ title: 'Eliminar documento', description: `¿Eliminar "${fullCodeOf(m)} ${m.title}"? Esta acción no se puede deshacer.`, confirmText: 'Eliminar' }))) return;
    try {
      await axios.delete(`/api/manuals/${m.id}`);
      toast.success('Documento eliminado');
      setManuals(prev => prev.filter(x => x.id !== m.id));
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const fileUrl = (m: Manual) => `/api/manuals/${m.id}/file?token=${localStorage.getItem('token') || ''}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-start">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Documentos</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Manuales, procedimientos, instructivos y formularios · norma MA-CAL-01</p>
        </div>
        {canManage && (
          <Button
            onClick={openNew}
            size="sm"
            className="inline-flex items-center justify-center whitespace-nowrap text-sm font-bold h-8 rounded-lg gap-1.5 px-3 bg-black dark:bg-white text-white dark:text-black shadow-xs hover:bg-black/90 dark:hover:bg-white/90"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Nuevo documento
          </Button>
        )}
      </div>

      {/* Buscador */}
      <div className="relative">
        <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
        <Input
          type="text"
          placeholder="Buscar por código o título..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="h-8 w-full sm:w-[320px] lg:w-[520px] pl-8 rounded-lg"
        />
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3">
        <div className="space-y-1 w-full sm:w-[220px]">
          <Label className="text-xs text-muted-foreground">Área</Label>
          <Select value={filterArea} onValueChange={setFilterArea}>
            <SelectTrigger className="h-8 rounded-lg"><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las áreas</SelectItem>
              {AREAS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {canManage && (
          <div className="space-y-1 w-full sm:w-[200px]">
            <Label className="text-xs text-muted-foreground">Estado</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-8 rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="vigente">Vigentes</SelectItem>
                <SelectItem value="obsoleto">Obsoletos</SelectItem>
                <SelectItem value="all">Todos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600"></div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <DocumentTextIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {search || filterArea !== 'all' ? 'No se encontraron resultados' : 'No hay documentos cargados'}
              </p>
            </div>
          ) : (
            <div className="relative w-full overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-muted/50 border-b">
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Código</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Título</TableHead>
                    <TableHead className="hidden md:table-cell text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Área</TableHead>
                    <TableHead className="hidden lg:table-cell text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Clasificación</TableHead>
                    <TableHead className="hidden sm:table-cell text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Estado</TableHead>
                    <TableHead className="hidden lg:table-cell text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Fecha</TableHead>
                    <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(m => {
                    const obsoleto = (m.status || 'vigente') === 'obsoleto';
                    return (
                      <TableRow key={m.id} className={`border-b hover:bg-muted/50 ${obsoleto ? 'opacity-70' : ''}`}>
                        <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                          <span className="font-mono text-sm font-medium">{fullCodeOf(m)}</span>
                        </TableCell>
                        <TableCell className="p-2 align-middle text-left max-w-[280px]">
                          <div className="font-medium truncate">{m.title}</div>
                          {m.description && <div className="text-xs text-muted-foreground truncate">{m.description}</div>}
                          {canManage && m.visibleToRoles && m.visibleToRoles.length > 0 && (
                            <div className="text-xs text-muted-foreground">🔒 {m.visibleToRoles.join(', ')}</div>
                          )}
                        </TableCell>
                        <TableCell className="hidden md:table-cell p-2 align-middle whitespace-nowrap text-left">
                          <Badge variant="secondary" className="text-xs">{AREA_LABEL[m.area || ''] || m.category}</Badge>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell p-2 align-middle whitespace-nowrap text-left">
                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${CLASS_BADGE[m.classification || 'uso_interno']}`}>
                            {CLASS_LABEL[m.classification || 'uso_interno']}
                          </span>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell p-2 align-middle whitespace-nowrap text-left">
                          {obsoleto
                            ? <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-medium">Obsoleto</span>
                            : <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-medium">Vigente</span>}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell p-2 align-middle whitespace-nowrap text-left">
                          {new Date(m.createdAt).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                        </TableCell>
                        <TableCell className="p-2 align-middle whitespace-nowrap text-right">
                          <div className="flex items-center justify-end">
                            <ButtonGroup>
                              <Tip label="Ver">
                                <Button variant="outline" size="icon" className="size-8" onClick={() => window.open(fileUrl(m), '_blank')}>
                                  <EyeIcon className="h-4 w-4" />
                                </Button>
                              </Tip>
                              <Tip label="Descargar">
                                <Button variant="outline" size="icon" className="size-8" onClick={() => window.open(`${fileUrl(m)}&download=1`, '_blank')}>
                                  <ArrowDownTrayIcon className="h-4 w-4" />
                                </Button>
                              </Tip>
                              {canManage && !obsoleto && m.tipo && (
                                <Tip label="Subir nueva revisión">
                                  <Button variant="outline" size="icon" className="size-8" onClick={() => openRevision(m)}>
                                    <ArrowPathIcon className="h-4 w-4" />
                                  </Button>
                                </Tip>
                              )}
                              {canManage && (
                                <Tip label="Eliminar">
                                  <Button variant="outline" size="icon" className="size-8" onClick={() => handleDelete(m)}>
                                    <TrashIcon className="h-4 w-4" />
                                  </Button>
                                </Tip>
                              )}
                            </ButtonGroup>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal: nuevo documento */}
      <Dialog open={showNew} onOpenChange={o => { if (!o) setShowNew(false); }}>
        <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuevo documento</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="space-y-1.5">
                <Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={v => setForm(f => ({ ...f, tipo: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TIPOS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Área</Label>
                <Select value={form.area} onValueChange={v => setForm(f => ({ ...f, area: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{AREAS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Nº</Label>
                <Input type="number" min={1} max={99} value={form.correlativo}
                  onChange={e => setForm(f => ({ ...f, correlativo: e.target.value }))} placeholder="auto" />
              </div>
              <div className="space-y-1.5">
                <Label>Rev.</Label>
                <Input type="number" min={0} max={99} value={form.revision}
                  onChange={e => setForm(f => ({ ...f, revision: e.target.value }))} placeholder="0" />
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              Código: <span className="font-mono font-medium text-foreground">
                {form.tipo}-{form.area}-{form.correlativo ? pad2(Number(form.correlativo)) : '··'}{Number(form.revision) >= 1 ? `-R${pad2(Number(form.revision))}` : ''}
              </span>
              <span className="ml-1">{Number(form.revision) >= 1 ? `(revisión R${pad2(Number(form.revision))})` : '(emisión inicial · sin revisión)'}</span>
            </div>
            <div className="space-y-1.5">
              <Label>Título *</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Restauración de ópticas..." />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Clasificación</Label>
                <Select value={form.classification} onValueChange={v => setForm(f => ({ ...f, classification: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CLASSIFICATIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Descripción (opcional)</Label>
                <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Breve..." />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Archivo *</Label>
              <input ref={newFileRef} type="file" className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.mp4,.mov"
                onChange={e => setNewFile(e.target.files?.[0] || null)} />
              <div onClick={() => newFileRef.current?.click()} className="flex items-center gap-3 border border-dashed border-border rounded-md px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors">
                <ArrowDownTrayIcon className="h-5 w-5 text-muted-foreground shrink-0" />
                <span className="text-sm text-muted-foreground">{newFile ? newFile.name : 'Seleccionar PDF, Word, imagen o video (máx. 30MB)'}</span>
              </div>
            </div>
            {availableRoles.length > 0 && (
              <div className="space-y-1.5">
                <Label>Visible para</Label>
                <p className="text-xs text-muted-foreground">Sin selección = según clasificación. Admin siempre ve todo.</p>
                <div className="flex flex-wrap gap-2">
                  {availableRoles.map(r => {
                    const checked = newRoles.includes(r.name);
                    return (
                      <label key={r.id} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border cursor-pointer text-sm select-none transition-colors ${checked ? 'bg-cyan-500/10 border-cyan-500/50 text-cyan-700 dark:text-cyan-400' : 'border-border hover:bg-muted'}`}>
                        <input type="checkbox" className="hidden" checked={checked}
                          onChange={() => setNewRoles(prev => prev.includes(r.name) ? prev.filter(x => x !== r.name) : [...prev, r.name])} />
                        {r.name}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancelar</Button>
            <Button onClick={handleUpload} disabled={uploading}>{uploading ? 'Subiendo...' : 'Crear documento'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: nueva revisión */}
      <Dialog open={!!revTarget} onOpenChange={o => { if (!o) setRevTarget(null); }}>
        <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nueva revisión</DialogTitle></DialogHeader>
          {revTarget && (
            <div className="space-y-4 py-1">
              <div className="rounded-md bg-muted/50 border border-border px-3 py-2 text-sm">
                <div className="font-mono font-medium">
                  {revTarget.code || `${revTarget.tipo}-${revTarget.area}-${pad2(revTarget.correlativo || 0)}`} · {(revTarget.revision || 0) >= 1 ? `R${pad2(revTarget.revision || 0)}` : 'inicial'} → <span className="text-emerald-600 dark:text-emerald-400">R{pad2((revTarget.revision || 0) + 1)}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">La versión {(revTarget.revision || 0) >= 1 ? `R${pad2(revTarget.revision || 0)}` : 'inicial'} quedará marcada como OBSOLETA (no se borra).</div>
              </div>
              <div className="space-y-1.5">
                <Label>Título</Label>
                <Input value={revForm.title} onChange={e => setRevForm(f => ({ ...f, title: e.target.value }))} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Clasificación</Label>
                  <Select value={revForm.classification} onValueChange={v => setRevForm(f => ({ ...f, classification: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CLASSIFICATIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Descripción</Label>
                  <Input value={revForm.description} onChange={e => setRevForm(f => ({ ...f, description: e.target.value }))} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Archivo de la nueva revisión *</Label>
                <input ref={revFileRef} type="file" className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.mp4,.mov"
                  onChange={e => setRevFile(e.target.files?.[0] || null)} />
                <div onClick={() => revFileRef.current?.click()} className="flex items-center gap-3 border border-dashed border-border rounded-md px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors">
                  <ArrowDownTrayIcon className="h-5 w-5 text-muted-foreground shrink-0" />
                  <span className="text-sm text-muted-foreground">{revFile ? revFile.name : 'Seleccionar el archivo actualizado'}</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevTarget(null)}>Cancelar</Button>
            <Button onClick={handleRevision} disabled={revUploading}>{revUploading ? 'Subiendo...' : 'Publicar revisión'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManualsPage;
